import {
  hashKey,
  MutationCache,
  QueryCache,
  QueryClient,
  type DefaultError,
  type DefaultedQueryObserverOptions,
  type DefaultOptions,
  type Query,
  type QueryKey,
  type QueryObserverOptions,
} from "@tanstack/react-query";
import { ApiError } from "@altek/ui/lib/problem";
import { onWriteSucceeded } from "./write-signal";

/**
 * The one QueryClient.
 *
 * A second one would give the application two caches that never see each other's
 * invalidations, so a mutation in one half of the screen would leave the other half showing
 * data it believes is fresh.
 */

/**
 * Freshness windows, by how fast the thing actually changes.
 *
 * The point of a stale time is not to save requests — it is to stop a screen flickering
 * through a loading state for data that was correct two seconds ago. Reference data (leave
 * types, document types, roles) changes a few times a year; a work queue changes while you
 * look at it.
 */
export const STALE = {
  /** Catalogue and configuration: leave types, document types, roles, permissions, units. */
  reference: 15 * 60 * 1000,
  /** Records a person edits: employees, policies, calendars. */
  record: 60 * 1000,
  /** Queues and dashboards: approval inbox, notifications, KPI counters. */
  live: 15 * 1000,
  /** Append-only history: a ledger page that has been written can never change. */
  immutable: Number.POSITIVE_INFINITY,
} as const;

/**
 * SAYFAYA HER GİRİŞTE VE HER YAZMADAN SONRA TAZE (1 Kasım 2026, kullanıcı: «herhangi bir işlemden sonra
 * sayfaya her girilince hangi sayfa olursa olsun refreshlesin datayı»).
 *
 * Önce: `refetchOnMount: false` — bir sayfaya geri dönülünce önbellekteki cevap, bayat olsa bile yeniden
 * istenmiyordu; yalnız işlemin kendisinin geçersiz kıldığı anahtarlar tazeleniyordu. Başka bir ekranda ya da
 * başka bir kullanıcının yaptığı iş (kongreyi biri tamamladı, öbürü listeye döndü) eski hâliyle görünüyordu.
 *
 * Şimdi iki kural, ikisi de eldeki satırları ekranda tutarak (önbellekte cevap varsa iskelet ya da boş durum
 * yok; tablo yeniden çekerken kitin `isFetching` soluklaşmasını gösteriyor):
 *
 * 1. SAYFA GİRİŞİ (`notePageEntry`): adresin YOLU değişince (`trackPageEntries`), girişten önce alınmış her
 *    cevap bayat sayılıyor; yeni sayfanın onu kullanan sorgusu bağlanınca (ya da aynı bileşen anahtarını ona
 *    çevirince) arka planda yeniden çekiliyor. Girişten en çok `PAGE_ENTRY_GRACE_MS` önce alınmış cevap taze
 *    sayılıyor: tetiğe basınca önceden alınan sekme ya da kartın sorgusu (`onIntent`) girer girmez ikinci kez
 *    gitmesin; o sırada yolda olan bir istek de bitince taze. Filtre ya da sayfa numarası (yalnız adres
 *    sorgusu) giriş değil — onlar zaten başka bir anahtar. Ekranda kalan kabuk sorguları da bayat sayılıyor
 *    ama bağlı oldukları için yeniden çekilmiyor; pencereye dönülünce çekiliyor.
 * 2. YAZMA (`staleAfterWrite`): başarılı her yazma (`write-signal.ts`) önbellekteki her şeyi bayat sayıyor;
 *    ekrandakiler hemen, öbürleri girilince yeniden çekiliyor.
 *
 * KATMANLAR:
 * - `immutable` (ve sonsuza dek taze olan her şey: oturum, okunuşu kayda geçen kişisel içerik) hiçbir kuralla
 *   yeniden çekilmiyor — yazılmış bir defter sayfası değişmez, kayıtlı bir okumayı kişi istemeden tekrarlamak
 *   da denetim günlüğüne onun yapmadığı bir okuma yazardı.
 * - `reference` (katalog: izin türleri, belge türleri, roller, birimler) GİRİŞTE yeniden çekilmiyor ve bir
 *   yazmadan sonra ekrandaysa HEMEN çekilmiyor; yalnız bayat sayılıyor, onu bağlayan sonraki ekran çekiyor.
 *   Gerekçe: katalog yılda birkaç kez ve yalnız kendi yönetim ekranından değişiyor — o ekranın mutasyonları
 *   kendi anahtarını zaten açıkça geçersiz kılıyor. Her girişte ya da alakasız her kayıttan sonra ekrandaki
 *   bütün açılır listeleri yeniden istemek yalnız istek sayısı; başka bir yöneticinin değişikliği de 15
 *   dakikalık pencere ve pencereye dönüş tazelemesiyle geliyor.
 * - `record` ve `live` her iki kuralla.
 */
export const PAGE_ENTRY_GRACE_MS = 2000;

/** Sorgunun tazelik penceresi; sayı değilse (`'static'` sonsuz, işlev kayıt gibi) ona göre. */
function staleTimeOf(query: Query): number | null {
  const value = (query.options as { staleTime?: unknown }).staleTime;
  if (value === "static") return Number.POSITIVE_INFINITY;
  return typeof value === "number" ? value : null;
}

/** Hiçbir kuralla yeniden çekilmeyen: defter sayfası, oturum, okunuşu kayda geçen içerik. */
function neverStale(query: Query): boolean {
  return staleTimeOf(query) === Number.POSITIVE_INFINITY;
}

/** Katalog katmanı (`STALE.reference`) ve ondan yavaş olan, sonsuz olmayan her şey. */
function isReference(query: Query): boolean {
  const staleTime = staleTimeOf(query);
  return staleTime !== null && staleTime >= STALE.reference && staleTime !== Number.POSITIVE_INFINITY;
}

/** Sayfa girişinde bayat sayılan: girişten (pay hariç) önce cevaplanmış `record`/`live` sorgusu. */
function answeredBeforeEntry(query: Query, at: number): boolean {
  if (neverStale(query) || isReference(query)) return false;
  const answeredAt = query.state.dataUpdatedAt;
  return answeredAt > 0 && answeredAt < at - PAGE_ENTRY_GRACE_MS;
}

/**
 * Bir sayfaya girildi (adresin yolu değişti): girişten önce alınmış `record`/`live` cevapları bayat. Yeniden
 * çekmiyor (`refetchType: "none"`) — çekmek, sorgu yeni sayfada bağlanınca `refetchOnMount: true` ile ya da
 * anahtarı ona dönünce oluyor. Sınamalar anı kendisi verebilir.
 */
export function notePageEntry(client: QueryClient, at: number = Date.now()): void {
  void client.invalidateQueries({ predicate: (query) => answeredBeforeEntry(query, at), refetchType: "none" });
}

/** Yönlendiricinin bu iş için gereken yüzü — `createBrowserRouter`'ın da sınamanın bellek yönlendiricisinin de. */
interface PageEntrySource {
  state: { location: { pathname: string } };
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void;
}

/**
 * Adresin YOLU her değişince bir sayfa girişi (`notePageEntry`). Yönlendiricinin aboneleri durum değişince
 * EŞZAMANLI çağrılıyor, yeni sayfa ondan sonra çiziliyor — cevaplar, yeni sayfanın sorguları bağlanmadan
 * bayat sayılmış oluyor. Dönen işlev aboneliği kaldırır.
 */
export function trackPageEntries(router: PageEntrySource, client: QueryClient): () => void {
  let pathname = router.state.location.pathname;
  return router.subscribe((state) => {
    if (state.location.pathname === pathname) return;
    pathname = state.location.pathname;
    notePageEntry(client);
  });
}

/**
 * Bir yazmadan sonra: sonsuza dek taze olanlar dışında her şey bayat; ekrandaki `record`/`live` sorguları
 * yeniden çekiliyor, katalog ve ekranda olmayanlar girilince.
 *
 * Yeniden çekme BİR SONRAKİ GÖREVDE ve süren isteği iptal etmeden (`cancelRefetch: false`): yazmanın kendi
 * mutasyonu `onSuccess`'te kendi anahtarlarını geçersiz kılıyor ve o istekler o sırada yoldadır — aynı sorgu
 * iki kez gitmiyor, birinin yarıda kesilip yeniden başlaması da yok. Bedeli: yazmadan ÖNCE başlamış ve hâlâ
 * süren bir istek (seyrek) yazmayı görmeyebilir; mutasyonun kendi anahtarları bu durumda da doğru.
 */
export function staleAfterWrite(client: QueryClient): void {
  void client.invalidateQueries({ predicate: (query) => !neverStale(query), refetchType: "none" });
  setTimeout(() => {
    void client.refetchQueries(
      { type: "active", stale: true, predicate: (query) => !neverStale(query) && !isReference(query) },
      { cancelRefetch: false },
    );
  }, 0);
}

/**
 * Retry only what retrying can fix.
 *
 * A 4xx was understood and refused; sending it again refuses it again, three times, while the
 * user watches a spinner. A 5xx or a dropped connection is worth one more attempt.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return false;
  if (!(error instanceof ApiError)) return failureCount < 1;
  return error.isRetryable && failureCount < 2;
}

/**
 * ESKİ SATIRLAR YALNIZ SAYFA ADIMINDA KALIR (31 Ekim 2026).
 *
 * İstemcinin varsayılanı `placeholderData: (previous) => previous` idi: bir sorgunun anahtarı
 * NE değişirse değişsin, yeni cevap gelene kadar eski veri ekranda kalıyordu. Sayfa 1'den 2'ye
 * geçerken doğru olan bu, filtre, arama, sıralama ya da şirket değişince yalan oluyordu — kişi
 * «Reddedildi» dilimine basıp bir an önceki listenin satırlarını, yeni filtrenin sonucu sanıyordu.
 * Pazarlama, not ve dönüştürücü listeleri bunu kendi sorgularında ayrı ayrı daraltmıştı; kural
 * artık burada, bir kez.
 *
 * Kural: önceki sorgunun anahtarı ile yenisi, içlerindeki `page` ve `limit` alanları bir yana
 * bırakılınca AYNIYSA bu bir sayfa adımıdır ve eski satırlar yerinde kalır (tablo kendi satır içi
 * bekleme hâlini gösterir). Başka her değişiklikte yer tutucu yok: liste iskelete ya da yükleniyor
 * hâline düşer. Kendi `placeholderData`'sını veren sorgu (kapatan da, kendi kuralını yazan da)
 * olduğu gibi kalır.
 */
const PAGE_STEP_FIELDS = new Set(["page", "limit"]);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
}

/**
 * TAKVİMİN AY ADIMI (31 Ekim 2026). Takvimde ay, listenin sayfası gibi bir gezinme: aynı kişinin,
 * aynı şirketin, aynı filtredeki takviminde ay değişince önceki ay yeni ay gelene kadar ekranda
 * kalır ve kit onu soluklaştırır (`isFetching`) — ızgara iskelete dönüp zıplamaz. Kişi, şirket ya da
 * filtre değişince kalmaz. Ay anahtarda NESNEDE durur: `{ from, to }`, izin yılında
 * `{ startsFrom, startsTo }` — konumuyla duran bir tarih ay sayılmaz (sayfa için de kural bu).
 */
const MONTH_STEP_FIELDS = new Set([...PAGE_STEP_FIELDS, "from", "to", "startsFrom", "startsTo"]);

/** Anahtarın listeyi söyleyen kısmı: listenin NE olduğu, içinde nerede durulduğu değil. */
function listIdentity(value: unknown, steps: ReadonlySet<string>): unknown {
  if (Array.isArray(value)) return value.map((entry) => listIdentity(entry, steps));
  if (!isPlainRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([field]) => !steps.has(field))
      .map(([field, entry]) => [field, listIdentity(entry, steps)]),
  );
}

function sameListApartFrom(steps: ReadonlySet<string>, previousKey: QueryKey, nextKey: QueryKey): boolean {
  return (
    hashKey(listIdentity(previousKey, steps) as QueryKey) === hashKey(listIdentity(nextKey, steps) as QueryKey)
  );
}

/** Yeni anahtar aynı listenin başka bir sayfası (ya da sayfa boyu) — başka hiçbir şey değişmedi. */
export function isPageStep(previousKey: QueryKey, nextKey: QueryKey): boolean {
  return sameListApartFrom(PAGE_STEP_FIELDS, previousKey, nextKey);
}

/** Yeni anahtar aynı takvimin başka bir ayı — kişi, şirket ve filtre aynı. */
export function isMonthStep(previousKey: QueryKey, nextKey: QueryKey): boolean {
  return sameListApartFrom(MONTH_STEP_FIELDS, previousKey, nextKey);
}

/**
 * Ay adımında ekranda kalan önceki ayın kayıtları, kitin takvimine verilmeden önce. Kit boş diziyi
 * «Bu ay kayıt yok» diye okuyor; önceki ay BOŞSA kalacak bir şey yok ve yeni ayın cevabı gelmeden
 * yeni ay için bunu söylemesin diye yükleniyor hâli (`undefined`). Ayın öbür cümleleri («bugün
 * izinli», «kısmen gösteriliyor») de `isPlaceholderData` iken söylenmiyor.
 */
export function shownMonthEntries<T>(entries: T[] | undefined, isPlaceholderData: boolean): T[] | undefined {
  return isPlaceholderData && entries?.length === 0 ? undefined : entries;
}

/**
 * `defaultOptions`'ta yazılı varsayılan. Kendi başına hiçbir şey tutmuyor: `AppQueryClient`
 * onu her sorgu için o sorgunun anahtarını bilen bir işlevle değiştiriyor. Bu varsayılanlarla
 * kurulan sıradan bir `QueryClient` (bazı sınamalar) bu yüzden başka listenin satırını değil,
 * yükleniyor hâlini gösterir.
 */
function pageStepOnly(): undefined {
  return undefined;
}

/**
 * Takvim sorgusunun yer tutucusu: `placeholderData: monthStepOnly` — önceki ay yalnız ay adımında
 * kalır (yukarıdaki kural). `pageStepOnly` gibi kendi başına hiçbir şey tutmuyor; `AppQueryClient`
 * onu sorgunun anahtarına bağlıyor.
 */
export function monthStepOnly(): undefined {
  return undefined;
}

/** İşaret → adım kuralı. */
const STEP_RULES = new Map<unknown, (previousKey: QueryKey, nextKey: QueryKey) => boolean>([
  [pageStepOnly, isPageStep],
  [monthStepOnly, isMonthStep],
]);

/**
 * Yer tutucu işlev yalnız ÖNCEKİ sorguyu görüyor (`previousData`, `previousQuery`) — şimdikinin
 * anahtarını değil. Sorgunun anahtarı seçeneklerin varsayılanlarla birleştiği tek yerde, burada
 * biliniyor; varsayılan yer tutucu (ve takvimin `monthStepOnly`'si) orada o anahtara bağlanıyor.
 */
class AppQueryClient extends QueryClient {
  override defaultQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    options:
      | QueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey, TPageParam>
      | DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
  ): DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
    const defaulted = super.defaultQueryOptions(options);
    const isStep = STEP_RULES.get(defaulted.placeholderData);
    if (!isStep) return defaulted;
    const queryKey = defaulted.queryKey;
    return {
      ...defaulted,
      placeholderData: (previous, previousQuery) =>
        previousQuery && isStep(previousQuery.queryKey, queryKey) ? previous : undefined,
    };
  }
}

const APP_DEFAULTS = {
  queries: {
    staleTime: STALE.record,
    gcTime: 10 * 60 * 1000,
    retry: shouldRetry,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    /**
     * Refetch when the tab comes back, because an ERP tab sits open for a whole shift and
     * the data behind it moves.
     */
    refetchOnWindowFocus: true,
    /**
     * A stale query refetches when it mounts — and a page entry or a write makes everything before it stale
     * (`notePageEntry`, `staleAfterWrite`, above). It used to be `false` — «navigation feel like it reloads
     * the page» — and a page came back showing data somebody had since changed. The rows on screen stay
     * while the refetch runs; there is no loading state to feel.
     */
    refetchOnMount: true,
    refetchOnReconnect: true,
    /**
     * Keeps the previous page's rows on screen while the next page of THE SAME list loads, so
     * paging does not blank the table; any other key change shows the loading state
     * (`AppQueryClient` above).
     */
    placeholderData: pageStepOnly,
  },
  mutations: {
    // A write is never retried automatically. Whether re-sending is safe depends on the
    // endpoint, and the ones where it is safe carry an idempotency key and ask explicitly.
    retry: false,
  },
} satisfies DefaultOptions;

/**
 * Uygulamanın istemcisini kurar; `overrides` sınamaların yeniden denemeyi kapatması gibi ayarlar
 * için. Uygulamada tek istemci var (`queryClient`); bu yalnız aynı davranışla bir ikincisini kurmak
 * isteyen sınamalar için dışarıda.
 */
export function createQueryClient(overrides: DefaultOptions = {}): QueryClient {
  return new AppQueryClient({
    defaultOptions: {
      queries: { ...APP_DEFAULTS.queries, ...overrides.queries },
      mutations: { ...APP_DEFAULTS.mutations, ...overrides.mutations },
    },
    /**
     * A session that expired mid-flight must not leave stale rows behind for the next person to
     * sign in — cache is cleared by the auth provider on sign-out, and these caches only carry
     * the error to the monitoring layer.
     */
    queryCache: new QueryCache(),
    mutationCache: new MutationCache(),
  });
}

export const queryClient = createQueryClient();

/*
  Uygulamanın istemcisi başarılı her yazmayı dinliyor (yukarıda, `staleAfterWrite`). Sınamaların kurduğu
  istemciler dinlemiyor: bir sınamanın yazması başka bir sınamanın önbelleğini bayatlatmasın; dinlemek isteyen
  sınama `onWriteSucceeded(() => staleAfterWrite(client))`'i kendisi çağırıyor.
*/
onWriteSucceeded(() => staleAfterWrite(queryClient));
